import React from "react";
// nodejs library that concatenates classes
import classNames from "classnames";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";

// @material-ui/icons
// core components
import Header from "components/Header/Header.js";
import Footer from "components/Footer/Footer.js";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import Parallax from "components/Parallax/Parallax.js";

import profile from "assets/img/ultragreen-logo-big.png";

import styles from "assets/jss/material-kit-react/views/profilePage.js";

import image1 from "assets/img/globe-02.png";

const useStyles = makeStyles(styles);


export default function PrivacyPage(props) {
  const classes = useStyles();
  const { ...rest } = props;
  const imageClasses = classNames(
    classes.imgRaised,
    classes.imgRoundedCircle,
    classes.imgFluid
  );

  return (
    <div>
      <Header
        color="transparent"
        brand="UltraGreen Certifications"
        rightLinks={<HeaderLinks />}
        fixed
        changeColorOnScroll={{
          height: 200,
          color: "white"
        }}
        {...rest}
      />
      <Parallax small filter image={require("assets/img/bg04.jpg")} />
      <div className={classNames(classes.main)}>
        <GridContainer justify="center">
          <GridItem xs={12} sm={12} md={8}>
            <div className={classes.profile}>
              <div>
                <img src={profile} alt="..." className={imageClasses} />
              </div>
            </div>
          </GridItem>
        </GridContainer>
        <div className={classes.container}>
          <GridContainer justify="center">
            <GridItem xs={8} sm={8} md={8}>
              <h1>Data Protection Policy</h1>
              <p><strong>Data Controller:</strong> UltraGreen Kft., 1039 Budapest, Batthyány utca 35/B., Hungary (Reg. 01-09-376313, VAT: HU28839792)</p>
              <p><strong>Last updated:</strong> April 2026</p>
              <p>UltraGreen Kft. ("UltraGreen", "we") processes the personal data it collects from users of this Website
              in accordance with Regulation (EU) 2016/679 (General Data Protection Regulation, GDPR).</p>

              <p><strong>PREAMBLE</strong></p>
              <p>This Website provides environmental certification services for cosmetics, household detergents, plastic granules, and packaging
              in compliance with EU Directive 2024/825 (Empowering Consumers Directive). This data protection policy describes:</p>
              <ul>
                <li>the types of personal data that UltraGreen may collect during the use of the Website, or during a first
        contact by phone, email or via the contact form,</li>
                <li>the purposes of data processing and their legal grounds,</li>
                <li>the possible recipients of the personal data,</li>
                <li>the storage period of the personal data, as well as,</li>
                <li>the rights attached to these personal data.</li>
              </ul>
              <p>UltraGreen has also developed a specific policy regarding the protection of the personal data of its
    partners, including (prospective) clients. This policy is available upon request.</p>

              <p><strong>1. TYPES OF PERSONAL DATA AND SOURCES</strong></p>
              <p>UltraGreen collects and processes only data which are strictly necessary for the purpose
    pursued (data minimization principle, GDPR Art. 5(1)(c)).</p>

              <p><em>1.1 Data you provide directly</em></p>
              <p>When contacting UltraGreen by phone, email or via the contact form, we may collect:</p>
              <ul>
                <li>Your name(s) and first name(s)</li>
                <li>Your personal and/or professional email address</li>
                <li>Your personal and/or professional phone number</li>
                <li>Your postal address or location</li>
                <li>Your company name and the product categories you wish to certify (cosmetics, detergents, packaging)</li>
              </ul>

              <p><em>1.2 Data collected automatically</em></p>
              <p>When you use this Website, the following data may be collected automatically:</p>
              <ul>
                <li><strong>Analytics data:</strong> We use Google Analytics 4 and Vercel Analytics to collect anonymized usage data
                such as pages visited, visit duration, referring source, device type, and approximate geographic location.
                Google Analytics may set cookies on your device. No personally identifiable information is sent to these services.</li>
                <li><strong>Search queries:</strong> When you use the Certification Verification (Claim Check) feature, your search
                terms (ingredient names, brand names, CAS/EC numbers) are sent to our backend service for processing.
                These queries are not linked to your identity and are not stored beyond the duration of the request.</li>
                <li><strong>QR code verification data:</strong> If you arrive via a QR code on a certified product, the URL parameter
                containing the brand or product identifier is processed to display verification results. This data is not stored.</li>
              </ul>

              <p><strong>2. PURPOSES AND LAWFULNESS OF PROCESSING PERSONAL DATA</strong></p>
              <p>Your data are processed by UltraGreen for the following purposes:</p>
              <ul>
                <li><strong>Responding to certification inquiries</strong> (legal basis: legitimate interest, GDPR Art. 6(1)(f)) —
                to follow up on your request submitted via the contact form, by email or by phone regarding certification
                of cosmetics, detergents, packaging, or plastic granules.</li>
                <li><strong>Certification verification</strong> (legal basis: legitimate interest, GDPR Art. 6(1)(f)) —
                to provide the public Claim Check service, enabling consumers and businesses to verify whether ingredients
                or brands meet UltraGreen certification requirements, in line with EU Directive 2024/825 transparency obligations.</li>
                <li><strong>Website analytics</strong> (legal basis: consent, GDPR Art. 6(1)(a)) —
                to understand how visitors use the Website and improve its functionality. Google Analytics 4 (provided by Google LLC)
                and Vercel Analytics (provided by Vercel Inc.) are used for this purpose.</li>
              </ul>

              <p><strong>3. RECIPIENTS AND THIRD-PARTY SERVICES</strong></p>
              <p>Your personal data may be shared with the following categories of recipients, solely for the purposes described above:</p>
              <ul>
                <li><strong>Vercel Inc.</strong> (United States) — Website hosting and analytics.</li>
                <li><strong>Google LLC</strong> (United States) — Analytics (Google Analytics 4) and contact form processing (Google Forms).</li>
                <li><strong>Proding</strong> — Backend service provider for certification database queries.</li>
              </ul>
              <p>Where data is transferred to the United States, appropriate safeguards are in place in accordance with
              GDPR Chapter V, including the EU-U.S. Data Privacy Framework where applicable.</p>

              <p><strong>4. HOW LONG DO WE STORE YOUR PERSONAL DATA?</strong></p>
              <p>UltraGreen stores your data only as long as necessary to fulfill the objective pursued at the time of collection:</p>
              <ul>
                <li><strong>Contact form submissions:</strong> retained for up to 3 years from your last interaction, or until you request deletion.</li>
                <li><strong>Analytics data:</strong> retained according to the default retention periods of Google Analytics 4 (14 months) and Vercel Analytics.</li>
                <li><strong>Search/verification queries:</strong> not stored beyond the duration of the request.</li>
              </ul>
              <p>In case UltraGreen would need your personal data for any purpose other than the one for which
              they were originally collected, UltraGreen will inform you and, if necessary, obtain your prior consent.</p>

              <p><strong>5. TRANSFER OF YOUR DATA OUTSIDE THE EU</strong></p>
              <p>As described in Section 3, certain third-party service providers (Google LLC, Vercel Inc.) are based in the United States.
              UltraGreen will transfer your personal data outside the European Union only when necessary to provide the services described
              in this policy, and only where adequate safeguards under GDPR Chapter V are in place.</p>

              <p><strong>6. YOUR RIGHTS UNDER GDPR</strong></p>
              <p>Pursuant to the General Data Protection Regulation, you have the following rights regarding your personal data:</p>
              <ul>
                <li><strong>Right of access</strong> (Art. 15) — obtain confirmation and a copy of your personal data</li>
                <li><strong>Right to rectification</strong> (Art. 16) — correct inaccurate data</li>
                <li><strong>Right to erasure</strong> (Art. 17) — request deletion of your data</li>
                <li><strong>Right to restriction of processing</strong> (Art. 18) — limit how we use your data</li>
                <li><strong>Right to data portability</strong> (Art. 20) — receive your data in a structured format</li>
                <li><strong>Right to object</strong> (Art. 21) — object to processing based on legitimate interest</li>
                <li><strong>Right to withdraw consent</strong> (Art. 7(3)) — withdraw consent at any time for analytics cookies</li>
              </ul>
              <p>You can exercise these rights by contacting our Data Protection Officer (DPO):</p>
              <ul>
                <li>by email: info@ultragreencert.com</li>
                <li>by post:<strong><br />
            UltraGreen Kft.<br /></strong>
        Batthyány utca 35/B.<br />
        1039 Budapest<br />
        Hungary</li>
              </ul>
              <p>All requests must be accompanied by a valid proof of identity. We will respond within 30 days of receiving your request.</p>

              <p><strong>7. RIGHT TO LODGE A COMPLAINT</strong></p>
              <p>If you believe that UltraGreen has not adequately addressed your data protection concerns, you have the right
              to lodge a complaint with the Hungarian National Authority for Data Protection and Freedom of Information
              (Nemzeti Adatvédelmi és Információszabadság Hatóság, NAIH), or with the supervisory authority of your EU Member State of residence.</p>

              <p><strong>8. CHANGES TO THIS POLICY</strong></p>
              <p>UltraGreen reserves the right to update this data protection policy. Any changes will be published on this page
              with an updated revision date. We encourage you to review this policy periodically.</p>
            </GridItem>
            <GridItem xs={12} sm={12} md={8}>
            <div className={classes.profile2}>
              <div>
                <img src={image1} alt="..." />
              </div>
            </div>
          </GridItem>
          </GridContainer>
        </div>
      </div>
      <Footer />
    </div>
  );
}
